import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { api } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { useI18n } from "../lib/i18n";
import type { AppConfig as AppConfigModel, AppConfigUpdate } from "../lib/types";
import { Button, Card, ErrorBox, Field, Loading } from "../components/ui";

/** Trạng thái form — giữ number dưới dạng string để ô nhập trống không nhảy về 0. */
interface FormState {
  versionCode: string;
  versionName: string;
  minVersionCode: string;
  linkAPK: string;
  releaseNotes: string;
  updateEnabled: boolean;
  autoCheckEnabled: boolean;
  checkIntervalMinutes: string;
  checkRounds: string;
  roundIntervalSeconds: string;
  webhookMaintenanceEnabled: boolean;
  webhookMaintenanceMessage: string;
}

function toForm(c: AppConfigModel): FormState {
  return {
    versionCode: String(c.versionCode),
    versionName: c.versionName,
    minVersionCode: String(c.minVersionCode),
    linkAPK: c.linkAPK ?? "",
    releaseNotes: c.releaseNotes ?? "",
    updateEnabled: c.updateEnabled,
    autoCheckEnabled: c.autoCheckEnabled,
    checkIntervalMinutes: String(c.checkIntervalMinutes),
    checkRounds: String(c.checkRounds),
    roundIntervalSeconds: String(c.roundIntervalSeconds),
    webhookMaintenanceEnabled: Boolean(c.webhookMaintenanceEnabled),
    webhookMaintenanceMessage: c.webhookMaintenanceMessage ?? "",
  };
}

export function AppConfig() {
  const { t, fmtDate } = useI18n();
  const { data, loading, error, reload } = useAsync(() => api.appConfig.getRaw(), []);
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Nạp dữ liệu server vào form một lần khi tải xong / sau khi lưu.
  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data]);

  if (loading || !form) {
    return (
      <div className="page">
        <header className="page-head">
          <div>
            <h1>{t("appcfg.title")}</h1>
            <p className="muted">{t("appcfg.subtitle")}</p>
          </div>
        </header>
        {error ? <ErrorBox message={error} /> : <Card><Loading /></Card>}
      </div>
    );
  }

  const set =
    (k: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm(f => (f ? { ...f, [k]: e.target.value } : f));
      setSaved(false);
    };

  const orNull = (v: string) => (v.trim() === "" ? null : v.trim());

  async function uploadApk(file: File) {
    if (!file.name.toLowerCase().endsWith(".apk")) {
      setActionError(t("appcfg.uploadApkInvalid"));
      return;
    }
    setUploading(true);
    setActionError(null);
    setUploaded(false);
    try {
      await api.appConfig.uploadApk(file);
      setUploaded(true);
      await reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  function onApkInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void uploadApk(file);
  }

  function onApkDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void uploadApk(file);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    setBusy(true);
    setActionError(null);
    setSaved(false);
    const payload: AppConfigUpdate = {
      versionCode: Number(form.versionCode),
      versionName: form.versionName.trim(),
      minVersionCode: Number(form.minVersionCode),
      linkAPK: orNull(form.linkAPK),
      releaseNotes: orNull(form.releaseNotes),
      updateEnabled: form.updateEnabled,
      autoCheckEnabled: form.autoCheckEnabled,
      checkIntervalMinutes: Number(form.checkIntervalMinutes),
      checkRounds: Number(form.checkRounds),
      roundIntervalSeconds: Number(form.roundIntervalSeconds),
      webhookMaintenanceEnabled: form.webhookMaintenanceEnabled,
      webhookMaintenanceMessage: orNull(form.webhookMaintenanceMessage),
    };
    try {
      await api.appConfig.update(payload);
      setSaved(true);
      await reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("appcfg.title")}</h1>
          <p className="muted">{t("appcfg.subtitle")}</p>
        </div>
      </header>

      {actionError && <ErrorBox message={actionError} />}
      {saved && <Alert severity="success" variant="outlined">{t("appcfg.saved")}</Alert>}

      <Card title={t("appcfg.section")}>
        <form onSubmit={submit} className="form">
          <div className="form-row">
            <Field label={t("appcfg.versionCode")} hint={t("appcfg.versionCodeHint")}>
              <input
                type="number"
                min={1}
                value={form.versionCode}
                onChange={set("versionCode")}
                required
              />
            </Field>
            <Field label={t("appcfg.versionName")} hint={t("appcfg.versionNameHint")}>
              <input value={form.versionName} onChange={set("versionName")} required />
            </Field>
          </div>

          <Field label={t("appcfg.minVersionCode")} hint={t("appcfg.minVersionCodeHint")}>
            <input
              type="number"
              min={1}
              value={form.minVersionCode}
              onChange={set("minVersionCode")}
              required
            />
          </Field>

          <Field label={t("appcfg.linkAPK")} hint={t("appcfg.linkAPKHint")}>
            <input
              type="url"
              inputMode="url"
              placeholder="https://…"
              value={form.linkAPK}
              onChange={set("linkAPK")}
            />
          </Field>

          <Field label={t("appcfg.uploadApk")} hint={t("appcfg.uploadApkHint")}>
            <Box
              onDragOver={e => {
                e.preventDefault();
                if (!uploading) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onApkDrop}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                p: 1.5,
                border: "2px dashed",
                borderColor: dragOver ? "primary.main" : "divider",
                borderRadius: 1,
                bgcolor: dragOver ? "action.hover" : "transparent",
                transition: "border-color .15s, background-color .15s",
              }}
            >
              <Button
                type="button"
                variant="default"
                loading={uploading}
                onClick={() => document.getElementById("apk-file-input")?.click()}
              >
                {t("appcfg.uploadApkAction")}
              </Button>
              <input
                id="apk-file-input"
                type="file"
                accept=".apk"
                hidden
                onChange={onApkInputChange}
                disabled={uploading}
              />
              <Typography variant="caption" color="text.secondary">
                {t("appcfg.uploadApkDropHint")}
              </Typography>
              {data?.linkAPK && (
                <a href={api.appConfig.apkDownloadUrl()} target="_blank" rel="noreferrer">
                  {data.apkOriginalName ?? t("appcfg.uploadApkCurrent")}
                </a>
              )}
            </Box>
          </Field>
          {uploaded && (
            <Alert severity="success" variant="outlined">
              {t("appcfg.uploadApkSaved")}
            </Alert>
          )}

          <Field label={t("appcfg.releaseNotes")} hint={t("appcfg.releaseNotesHint")}>
            <textarea rows={3} value={form.releaseNotes} onChange={set("releaseNotes")} />
          </Field>

          <FormControlLabel
            control={
              <Switch
                checked={form.updateEnabled}
                onChange={e => {
                  setForm(f => (f ? { ...f, updateEnabled: e.target.checked } : f));
                  setSaved(false);
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("appcfg.updateEnabled")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("appcfg.updateEnabledHint")}
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", m: 0 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.autoCheckEnabled}
                onChange={e => {
                  setForm(f => (f ? { ...f, autoCheckEnabled: e.target.checked } : f));
                  setSaved(false);
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("appcfg.autoCheckEnabled")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("appcfg.autoCheckEnabledHint")}
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", m: 0 }}
          />

          <div className="form-row">
            <Field label={t("appcfg.checkIntervalMinutes")} hint={t("appcfg.checkIntervalMinutesHint")}>
              <input
                type="number"
                min={1}
                max={1440}
                value={form.checkIntervalMinutes}
                onChange={set("checkIntervalMinutes")}
                required
              />
            </Field>
            <Field label={t("appcfg.checkRounds")} hint={t("appcfg.checkRoundsHint")}>
              <input
                type="number"
                min={1}
                max={20}
                value={form.checkRounds}
                onChange={set("checkRounds")}
                required
              />
            </Field>
          </div>

          <Field label={t("appcfg.roundIntervalSeconds")} hint={t("appcfg.roundIntervalSecondsHint")}>
            <input
              type="number"
              min={0}
              max={3600}
              value={form.roundIntervalSeconds}
              onChange={set("roundIntervalSeconds")}
              required
            />
          </Field>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t("appcfg.webhookSection")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("appcfg.webhookSectionHint")}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                color="warning"
                checked={form.webhookMaintenanceEnabled}
                onChange={e => {
                  setForm(f => (f ? { ...f, webhookMaintenanceEnabled: e.target.checked } : f));
                  setSaved(false);
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("appcfg.webhookMaintenanceEnabled")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("appcfg.webhookMaintenanceEnabledHint")}
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", m: 0 }}
          />

          {form.webhookMaintenanceEnabled ? (
            <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                {t("appcfg.webhookMaintenanceActive")}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                {t("appcfg.webhookMaintenanceInactive")}
              </Typography>
            </Alert>
          )}

          <Field
            label={t("appcfg.webhookMaintenanceMessage")}
            hint={t("appcfg.webhookMaintenanceMessageHint")}
          >
            <input
              type="text"
              placeholder={t("appcfg.webhookMaintenanceMessagePlaceholder")}
              value={form.webhookMaintenanceMessage}
              onChange={set("webhookMaintenanceMessage")}
            />
          </Field>

          <Divider sx={{ my: 1.5 }} />

          {/* Diễn giải cấu hình hiện tại — giúp admin thấy trước hệ quả trước khi lưu. */}
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
              {t("appcfg.previewTitle")}
            </Typography>
            <Typography variant="caption">
              {form.updateEnabled
                ? t("appcfg.previewOn", {
                    min: form.minVersionCode || "?",
                    name: form.versionName || "?",
                  })
                : t("appcfg.previewOff")}
            </Typography>
          </Alert>

          <div className="form-actions">
            {data && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
                {t("appcfg.updatedAt")}: {fmtDate(data.updatedAt)}
              </Typography>
            )}
            <Button type="submit" variant="primary" loading={busy}>
              {t("appcfg.save")}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
